import AcButton from '@/dashboard/ui/AcButton';
import GlobalPatientSearchModal from '@/dashboard/patients/GlobalPatientSearchModal';
import { createContext, useCallback, useContext, useEffect, useRef, useState, type MouseEvent } from 'react';
import { Link, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { buttonVariants } from '@/components/ui/button';
import { useAuth } from '@/auth/auth-context';
import { fetchDashboardMe } from '@/dashboard/api';
import {
  ClinicContextProvider,
  clinicOptionLabel,
  useClinicContext,
} from '@/dashboard/clinic-context';
import { roleLabel } from '@/dashboard/ui/role-presentation';
import RouteErrorBoundary from '@/dashboard/ui/RouteErrorBoundary';
import type { DashboardMe } from '@/dashboard/types';

type BeforeRoleNavigation = () => Promise<boolean>;
type RegisterRoleNavigation = (guard: BeforeRoleNavigation) => () => void;
const NOOP_REGISTER: RegisterRoleNavigation = () => () => undefined;
const RoleNavigationContext = createContext<RegisterRoleNavigation>(NOOP_REGISTER);

/** The active consult joins its existing writers before shared-shell exits. */
export function useRegisterRoleNavigationGuard(): RegisterRoleNavigation {
  return useContext(RoleNavigationContext);
}

interface AppLayoutHeaderProps {
  /** Authenticated user (id + email) — null when signed out. */
  readonly user: { id: string; email?: string | null } | null;
  readonly loading: boolean;
  readonly authConfigured: boolean;
  /**
   * Cached `/api/dashboard/me` payload for the signed-in user, or
   * null when anonymous / still loading / errored. Drives the
   * role-aware Dashboard link target.
   */
  readonly me: DashboardMe | null;
  readonly onSignOut: () => void;
  readonly beforeClinicChange?: () => Promise<boolean>;
}

/**
 * Resolve where the brand wordmark and Home nav link should point.
 *
 *  - Signed-out: `/home` (the public marketing page).
 *  - Signed-in with `/me` loaded: `me.homePath` (role-aware — owner sees
 *    `/dashboard/owner`, doctor sees `/dashboard/doctor`, receptionist
 *    sees `/walkin`, etc.).
 *  - Signed-in but `/me` still loading: `/dashboard`. The
 *    `DashboardHomeRedirect` rendered at that route does its own
 *    role-aware redirect once `me` resolves, so a receptionist briefly
 *    landing on `/dashboard` will not see an owner page.
 *
 * Hardening: never returns a hardcoded `/dashboard/owner` for a
 * signed-in user — that's the same leak the role-aware Dashboard link
 * was originally fixed for.
 */
function getHomeTarget(
  user: AppLayoutHeaderProps['user'],
  me: AppLayoutHeaderProps['me'],
): string {
  if (!user) return '/home';
  if (me && me.homePath) return me.homePath;
  return '/dashboard';
}

/**
 * Pure presentational header — exported so tests can render it with
 * controlled props via `react-dom/server` without a live
 * `/api/dashboard/me` or `AuthProvider`. The Dashboard link target
 * comes from `me.homePath` (role-aware) and is hidden entirely when
 * the role has no dashboard nav sections (e.g. `patient`) so we never
 * expose owner URLs in the global header to lower roles.
 */
export function AppLayoutHeader({
  user,
  loading,
  authConfigured,
  me,
  onSignOut,
  beforeClinicChange,
}: AppLayoutHeaderProps): JSX.Element {
  const location = useLocation();
  const clinic = useClinicContext();
  const moneySearchEnabled = !loading && user !== null && me?.role === 'receptionist'
    && location.pathname === '/dashboard/money';
  const [moneyQuery, setMoneyQuery] = useState('');
  const [moneySearchOpen, setMoneySearchOpen] = useState(false);
  const moneyInput = useRef<HTMLInputElement>(null);
  useEffect(() => { setMoneyQuery(''); setMoneySearchOpen(false); },
    [location.pathname, clinic?.selectedClinicId, user?.id, me?.role]);
  const showDashboardLink = me !== null && me.navSections.length > 0;
  // F1 (RECEPTIONIST-EXPERIENCE-STITCH-V1): a signed-in dashboard operator gets
  // their nav ONCE, in the sidebar. The top-bar Home / Walk-in / Dashboard links
  // were "four doors to one room" (all resolved to the same home for a
  // receptionist), so we hide the top-bar nav for dashboard users and keep only
  // brand + identity.
  void showDashboardLink;
  // F14 (STITCH-2 rider (e)) -- FAIL-CLOSED header: the public marketing links
  // render ONLY after a CONFIRMED signed-out state. While auth is loading, or
  // while a signed-in user's /me is still resolving, the header is brand-only
  // (no public nav, no Log-in flash). A signed-in user never sees the public
  // nav at all -- their nav lives in the sidebar.
  const confirmedSignedOut = !loading && user === null;
  const showPublicNav = confirmedSignedOut;
  const homeTarget = getHomeTarget(user, me);
  const compactAccount = me?.role === 'doctor' || me?.role === 'receptionist';
  const accountRef = useRef<HTMLDetailsElement>(null);
  // Native disclosure retains keyboard semantics; Escape and outside clicks
  // return to the actual opener. Signing out still joins the consult writer.
  useEffect(() => {
    if (!compactAccount) return;
    const close = (event: KeyboardEvent | PointerEvent) => {
      const menu = accountRef.current;
      if (!menu?.open) return;
      if (event instanceof KeyboardEvent) {
        if (event.key !== 'Escape') return;
        menu.open = false;
        menu.querySelector('summary')?.focus();
      } else if (event.target instanceof Node && !menu.contains(event.target)) menu.open = false;
    };
    document.addEventListener('keydown', close);
    document.addEventListener('pointerdown', close);
    return () => {
      document.removeEventListener('keydown', close);
      document.removeEventListener('pointerdown', close);
    };
  }, [compactAccount]);
  const accountIdentity = user?.email ?? user?.id ?? '';
  const initials = accountIdentity.split('@')[0].split(/[.\s_-]+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase();
  return (
    <header data-role-home={compactAccount ? 'true' : undefined} className="ac-app-topbar sticky top-0 z-40 flex flex-wrap items-center gap-3 border-b border-border bg-card px-4 py-3">
      <Link
        to={homeTarget}
        className="text-lg text-primary font-bold tracking-tight"
      >
        AdoraCare
      </Link>
      {/* Role-home search mounts here from the active Doctor or Reception page.
          The page keeps its existing role-fenced search and result modal. */}
      {!loading && user && me?.navSections.length ? (
        <div id="role-home-search-slot" className="role-home-search-slot">
          {moneySearchEnabled ? <section className="today-home__hero" aria-label="Find the patient">
            <form className="today-home__search" onSubmit={event => {
              event.preventDefault(); if (moneyQuery.trim().length >= 2) setMoneySearchOpen(true);
            }}>
              <input ref={moneyInput} className="today-home__search-input" type="search"
                aria-label="Search patients" placeholder="Name, mobile, UMR or appointment number"
                value={moneyQuery} onChange={event => setMoneyQuery(event.target.value)} autoComplete="off" />
              <AcButton variant="primary" type="submit" disabled={moneyQuery.trim().length < 2}>Search</AcButton>
            </form>
          </section> : null}
          {moneySearchEnabled && moneySearchOpen ? <GlobalPatientSearchModal
            key={`${clinic?.selectedClinicId}:${user?.id}`} initialQuery={moneyQuery} audience="desk" recordOrigin="money"
            onClose={() => { setMoneySearchOpen(false); requestAnimationFrame(() => moneyInput.current?.focus()); }} /> : null}
        </div>
      ) : null}
      {showPublicNav ? (
        <nav
          className="flex flex-1 flex-wrap items-center gap-1"
          aria-label="Main"
        >
          <NavLink
            to={homeTarget}
            className={({ isActive }) =>
              [
                'rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              ].join(' ')
            }
          >
            Home
          </NavLink>
          <Link
            to="/walkin"
            className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            Walk-in
          </Link>
        </nav>
      ) : null}
      <div className="ml-auto flex items-center gap-2">
        {/* F11 (STITCH-2 SCOPE 11): the global clinic context lives in the
            top-right identity cluster (the banked mount point). */}
        {!loading && user ? <HeaderClinicContext beforeChange={beforeClinicChange} /> : null}
        {!loading && user ? (
          compactAccount ? <details ref={accountRef} className="ac-account-menu">
            <summary aria-label="Account menu" title="Account menu">{initials || 'Account'}</summary>
            <div className="ac-account-menu__content">
              <strong>{accountIdentity}</strong>
              <span data-testid="header-role-pill">{roleLabel(me!.role)}</span>
              <button type="button" className={buttonVariants({ variant: 'secondary', size: 'sm' })} onClick={onSignOut}>Sign out</button>
            </div>
          </details> : <>
            {/* UNIFIED-SHELL-AND-ROLE-NAVIGATION-V1 (V7-P4 Slice 2): current-role
                presentation in the shell identity cluster. The label is resolved
                from server truth (me.role), shown for dashboard operators (a role
                with nav sections); a role pill is a LABEL, never an authority
                grant. A signed-in patient (no nav sections) keeps brand-only
                chrome, unchanged. */}
            {me && me.navSections.length > 0 ? (
              <span
                className="hidden rounded-full border border-border px-2 py-0.5 text-xs font-medium text-muted-foreground sm:inline"
                data-testid="header-role-pill"
                title="Your current role"
              >
                {roleLabel(me.role)}
              </span>
            ) : null}
            <span className="ac-header-email hidden max-w-[200px] truncate text-sm text-muted-foreground sm:inline">
              {user.email ?? user.id}
            </span>
            <button
              type="button"
              className={buttonVariants({ variant: 'secondary', size: 'sm' })}
              onClick={onSignOut}
            >
              Sign out
            </button>
          </>
        ) : confirmedSignedOut ? (
          <Link to="/login" className={buttonVariants({ size: 'sm' })}>
            Log in
          </Link>
        ) : null}
        {!authConfigured && !loading ? (
          <span
            className="text-xs text-muted-foreground"
            title="Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY"
          >
            Auth env missing
          </span>
        ) : null}
      </div>
    </header>
  );
}

/**
 * The header clinic selector: fixed name for single-clinic staff, an
 * assigned-clinics picker for multi-clinic roles, All clinics for the
 * owner. Labels only -- no raw clinic_id ever renders.
 */
export function HeaderClinicContext({beforeChange}:{beforeChange?:()=>Promise<boolean>} = {}): JSX.Element | null {
  const ctx = useClinicContext();
  const current = useRef(ctx); current.current = ctx;
  const [changing,setChanging] = useState(false);
  const changingRef = useRef(false);
  const mounted = useRef(true);
  useEffect(()=>{mounted.current=true;return()=>{mounted.current=false;};},[]);
  if (ctx === null || ctx.mode === 'none') return null;
  if (ctx.mode === 'fixed') {
    return (
      <span
        className="hidden text-sm font-medium text-foreground sm:inline"
        data-testid="header-clinic-fixed"
      >
        {ctx.selectedLabel}
      </span>
    );
  }
  return (
    <label className="flex items-center gap-1 text-sm" data-testid="header-clinic-picker">
      <span className="sr-only">Clinic</span>
      <select
        className="rounded-md border border-border bg-card px-2 py-1 text-sm"
        value={ctx.selectedClinicId === null ? '' : String(ctx.selectedClinicId)}
        disabled={changing}
        onChange={(e) => {
          const v = e.target.value;
          const selected = v === '' ? null : Number(v);
          if (changingRef.current || selected === ctx.selectedClinicId) return;
          if (!beforeChange) { ctx.select(selected); return; }
          const original = ctx;
          changingRef.current=true; setChanging(true);
          void beforeChange().then(ready=>{
            if (ready && mounted.current && current.current === original) original.select(selected);
          }).catch(()=>undefined).finally(()=>{
            changingRef.current=false; if(mounted.current)setChanging(false);
          });
        }}
      >
        <option value="">All clinics</option>
        {ctx.options.map((o) => (
          <option key={o.clinicId} value={String(o.clinicId)}>
            {clinicOptionLabel(o)}
          </option>
        ))}
      </select>
    </label>
  );
}

export default function AppLayout() {
  const { user, loading, authConfigured, signOut } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState<DashboardMe | null>(null);
  const beforeNavigation = useRef<BeforeRoleNavigation | null>(null);
  const registerNavigation = useCallback<RegisterRoleNavigation>((guard) => {
    beforeNavigation.current = guard;
    return () => {
      if (beforeNavigation.current === guard) beforeNavigation.current = null;
    };
  }, []);

  const onSharedNavigationCapture = useCallback((event: MouseEvent<HTMLDivElement>) => {
    const guard = beforeNavigation.current;
    if (guard === null || event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const link = target.closest<HTMLAnchorElement>('a[href]');
    if (link === null || link.target && link.target !== '_self' || link.hasAttribute('download')) return;
    if (link.closest('.ac-app-topbar, .dashboard-nav') === null) return;
    const destination = new URL(link.href, window.location.href);
    if (destination.origin !== window.location.origin) return;
    const to = destination.pathname + destination.search + destination.hash;
    if (to === window.location.pathname + window.location.search + window.location.hash) return;
    event.preventDefault();
    event.stopPropagation();
    void guard().then((saved) => {
      if (saved) navigate(to);
    }).catch(() => undefined); // A failed writer leaves the current editor mounted.
  }, [navigate]);

  // Fetch `/api/dashboard/me` only when the user is signed in. The
  // header's Dashboard link uses `me.homePath` so a receptionist sees
  // `/walkin`, a doctor sees `/dashboard/doctor`, an owner sees
  // `/dashboard/owner`, and a patient (no nav sections) sees no link.
  // On error the link stays hidden — better to drop the link than
  // leak the hardcoded `/dashboard/owner` of the prior implementation.
  useEffect(() => {
    if (!user) {
      setMe(null);
      return;
    }
    let cancelled = false;
    fetchDashboardMe()
      .then((value) => {
        if (!cancelled) setMe(value);
      })
      .catch(() => {
        if (!cancelled) setMe(null);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  return (
    <ClinicContextProvider me={me}>
      <RoleNavigationContext.Provider value={registerNavigation}>
      <div className="flex min-h-screen flex-col" onClickCapture={onSharedNavigationCapture}>
        <AppLayoutHeader
          user={user ? { id: user.id, email: user.email ?? null } : null}
          loading={loading}
          authConfigured={authConfigured}
          me={me}
          beforeClinicChange={async()=>beforeNavigation.current===null || await beforeNavigation.current()}
          onSignOut={() => {
            void (async () => {
              try {
                if (beforeNavigation.current === null || await beforeNavigation.current()) await signOut();
              } catch {
                // Keep the clinical editor and its visible save error in place.
              }
            })();
          }}
        />
        {/* APP-ROUTE-ERROR-BOUNDARY-V1: a render crash below the header
            (including inside DashboardLayout itself) is contained here so
            the top bar -- brand, identity cluster, a real Sign out --
            survives. Pass-through markup when healthy. */}
        <RouteErrorBoundary>
          <Outlet />
        </RouteErrorBoundary>
      </div>
      </RoleNavigationContext.Provider>
    </ClinicContextProvider>
  );
}
